/**
 * Scroll-driven scrub for text animations inside page overlays.
 *
 * Same idea as the grid `scrub.js` — but the page-overlay pipeline uses its
 * own timeline factory + map (see `textanimations_pageoverlays.js`), and a
 * different split event (`texts_are_split_for_page_overlays`). We mirror the
 * grid controller here so a target inside `.lay-overlay` builds its
 * timeline against the correct map and gets cleaned up on the right resize/
 * resplit signal.
 *
 * The grid `scrub.js` filters out anything inside `.lay-overlay`, so the two
 * controllers don't overlap.
 *
 * For per-type rationale (timeline.seek vs per-char opacity) see scrub.js.
 */

import { getProgress, getNaturalViewportRect, getScrollContainerForElement, getViewportContext } from '../../scroll_animations/viewport_progress'
import { createAnimationTimeline, animationTimelines } from './textanimations_pageoverlays'
import { setLineTextMaskRevealClip } from '../util'
import { getWordLevelAnimationTargets, getCharacterLevelAnimationTargets } from '../text_animation_targets'
import { applyPreRevealVisualStateToTarget } from '../text_animation_resplit_recovery'
import {
	clearTypewriterBackgroundReveal,
	prepareTypewriterBackgroundReveal,
	setTypewriterBackgroundRevealProgress,
} from '../typewriter_background_reveal'

const SCRUB_ATTR = 'data-animation-scrub'
const START_AT_ATTR = 'data-animation-scrub-start-at'
const END_AT_TOP_ATTR = 'data-animation-scrub-end-at-top'
const END_AT_FULL_EXIT_ATTR = 'data-animation-scrub-end-at-full-exit'
const NEAR_VIEWPORT_MARGIN_VH = 0.5
const SCROLL_IDLE_TIMEOUT_MS = 600

const pairings = new Map()
let rafId = null
let introRafId = null
let scrollIdleTimeoutId = null
let listenersAttached = false
const scrollRootsWithListeners = new Set()
let prefersReducedMotion = false

function isRectNearViewport(rect, vh) {
	const margin = vh * NEAR_VIEWPORT_MARGIN_VH
	return rect.top < vh + margin && rect.bottom > -margin
}

function directionForType(target, animationType) {
	const direction = target.getAttribute('data-direction')
	if (direction === 'left' || direction === 'right' || direction === 'up' || direction === 'down' || direction === 'bottomLeft' || direction === 'bottomRight') {
		return direction
	}
	if (animationType === 'typewriter_words' || animationType === 'typewriter') {
		return 'left'
	}
	return 'up'
}

function shouldReverseTypewriterScrub(target, animationType) {
	const readingDirection = target.getAttribute('data-reading-direction')
	if (readingDirection === 'rtl') {
		return true
	}
	if (readingDirection === 'ltr') {
		return false
	}
	if (animationType === 'typewriter_lines') {
		return directionForType(target, animationType) === 'down'
	}
	return directionForType(target, animationType) === 'right'
}

function applyTypewriterFadeProgress(pairing, progress) {
	const chars = pairing.characters
	if (!chars) return
	const n = chars.length
	if (n === 0) return
	const t = progress * pairing.totalDuration
	const ease = pairing.easingFn
	const baseDelay = pairing.baseDelay
	const initialDelay = pairing.initialDelay
	const tweenDuration = pairing.tweenDuration

	let cache = pairing._lastOpacities
	if (!cache || cache.length !== n) {
		cache = new Array(n).fill(-1)
		pairing._lastOpacities = cache
	}

	// See scrub.js for full rationale — direct opacity math + per-char cache
	// avoids anime.js's per-tick full-sweep over every char animation.
	for (let i = 0; i < n; i++) {
		const charStart = initialDelay + i * baseDelay
		const charEnd = charStart + tweenDuration
		let op
		if (t <= charStart) op = 0
		else if (t >= charEnd) op = 1
		else op = ease((t - charStart) / tweenDuration)
		const rounded = Math.round(op * 1000) / 1000
		if (rounded !== cache[i]) {
			chars[i].style.opacity = rounded === 0 ? '0' : rounded === 1 ? '1' : String(rounded)
			cache[i] = rounded
		}
	}
}

function applyTypewriterProgress(target, pairing, progress) {
	const characters = pairing.characters
	if (!characters) return
	const n = characters.length
	if (n === 0) return
	let visibleCount = 0
	for (let i = 0; i < n; i++) {
		if (progress > i / n) visibleCount = i + 1
	}
	const last = pairing._lastVisibleCount | 0
	if (last === visibleCount) return
	const lo = pairing.reverse
		? n - (last < visibleCount ? visibleCount : last)
		: (last < visibleCount ? last : visibleCount)
	const hi = pairing.reverse
		? n - (last < visibleCount ? last : visibleCount)
		: (last < visibleCount ? visibleCount : last)
	const opacityShown = visibleCount > last ? '1' : '0'
	for (let i = lo; i < hi; i++) {
		characters[i].style.opacity = opacityShown
	}
	pairing._lastVisibleCount = visibleCount
	setTypewriterBackgroundRevealProgress(target, visibleCount, pairing.reverse)
}

function applyTimelineProgress(timeline, progress) {
	if (!timeline) return
	const dur = typeof timeline.duration === 'number' ? timeline.duration : 0
	if (dur <= 0) return
	if (!timeline.paused) timeline.pause()
	timeline.seek(progress * dur)
}

function applyPairingProgress(target, pairing, progress) {
	if (pairing.type === 'typewriter') {
		applyTypewriterProgress(target, pairing, progress)
	} else if (pairing.type === 'typewriter_fade') {
		applyTypewriterFadeProgress(pairing, progress)
	} else {
		applyTimelineProgress(pairing.timeline, progress)
	}
}

function updatePairing(target, pairing, rect, ctx) {
	const progress = getProgress(rect, pairing.endAtTop, pairing.endAtFullExit, pairing.startAt, ctx)
	applyPairingProgress(target, pairing, progress)
}

function snapPairingToEnd(target, pairing) {
	applyPairingProgress(target, pairing, 1)
}

function getCurrentProgressForPairing(target, pairing) {
	const rect = getNaturalViewportRect(target)
	const ctx = getViewportContext(getScrollContainerForElement(target))
	return getProgress(rect, pairing.endAtTop, pairing.endAtFullExit, pairing.startAt, ctx)
}

function cancelIntroAnimation() {
	if (introRafId !== null) {
		cancelAnimationFrame(introRafId)
		introRafId = null
	}
}

function getIntroDurationMs() {
	const configured = window.layData ? parseInt(window.layData.navigation_transition_in_duration, 10) : 0
	if (isFinite(configured) && configured > 0) {
		return Math.min(Math.max(configured, 220), 700)
	}
	return 360
}

function easeIntroProgress(progress) {
	if (typeof anime !== 'undefined' && typeof anime.easing === 'function') {
		return anime.easing('easeOutCubic')(progress)
	}
	return 1 - Math.pow(1 - progress, 3)
}

function animateInitialPairingsToCurrentProgress(targets) {
	cancelIntroAnimation()
	const items = []
	targets.forEach((target) => {
		const pairing = pairings.get(target)
		if (!target || !pairing) return
		const to = getCurrentProgressForPairing(target, pairing)
		applyPairingProgress(target, pairing, 0)
		if (to > 0) {
			items.push({ target, pairing, to })
		}
	})
	if (items.length === 0) {
		onScrollOrResize()
		return
	}

	const duration = getIntroDurationMs()
	const start = performance.now()
	const step = (now) => {
		const t = Math.min(1, (now - start) / duration)
		const eased = easeIntroProgress(t)
		items.forEach(({ target, pairing, to }) => {
			if (!target || !target.isConnected || !pairings.has(target)) return
			applyPairingProgress(target, pairing, to * eased)
		})
		if (t < 1) {
			introRafId = requestAnimationFrame(step)
			return
		}
		introRafId = null
		onScrollOrResize()
	}
	introRafId = requestAnimationFrame(step)
}

const tick = () => {
	let anyNear = false
	for (const [target, pairing] of pairings) {
		if (!target || !target.isConnected) {
			clearTypewriterBackgroundReveal(target)
			pairings.delete(target)
			continue
		}
		const rect = getNaturalViewportRect(target)
		const ctx = getViewportContext(getScrollContainerForElement(target))
		if (!isRectNearViewport(rect, ctx.vh)) continue
		anyNear = true
		updatePairing(target, pairing, rect, ctx)
	}
	if (anyNear && scrollIdleTimeoutId !== null) {
		rafId = requestAnimationFrame(tick)
	} else {
		rafId = null
	}
}

const ensureLoopRunning = () => {
	if (rafId == null) {
		rafId = requestAnimationFrame(tick)
	}
}

const onScrollOrResize = () => {
	cancelIntroAnimation()
	if (scrollIdleTimeoutId !== null) {
		clearTimeout(scrollIdleTimeoutId)
	}
	scrollIdleTimeoutId = window.setTimeout(() => {
		scrollIdleTimeoutId = null
	}, SCROLL_IDLE_TIMEOUT_MS)
	ensureLoopRunning()
}

const attachListeners = () => {
	if (listenersAttached) return
	listenersAttached = true
	// Capture-phase: page overlays scroll inside `.lay-overlay > .lay-content`
	// and `scroll` doesn't bubble.
	window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true })
	// See note in scrub.js — `window_width_changed` ignores iOS chrome
	// show/hide noise.
	jQuery(window).on('window_width_changed', onScrollOrResize)
}

const syncScrollRootListeners = () => {
	const wantedScrollRoots = new Set()
	for (const target of pairings.keys()) {
		const scrollRoot = getScrollContainerForElement(target)
		if (scrollRoot && scrollRoot !== window) {
			wantedScrollRoots.add(scrollRoot)
		}
	}

	wantedScrollRoots.forEach((scrollRoot) => {
		if (scrollRootsWithListeners.has(scrollRoot)) {
			return
		}
		scrollRoot.addEventListener('scroll', onScrollOrResize, { passive: true })
		scrollRootsWithListeners.add(scrollRoot)
	})

	scrollRootsWithListeners.forEach((scrollRoot) => {
		if (wantedScrollRoots.has(scrollRoot)) {
			return
		}
		scrollRoot.removeEventListener('scroll', onScrollOrResize)
		scrollRootsWithListeners.delete(scrollRoot)
	})
}

function buildPairing(target) {
	const animType = target.getAttribute('data-animation-type') || ''
	const startAtRaw = parseFloat(target.getAttribute(START_AT_ATTR))
	const startAt = isFinite(startAtRaw) && startAtRaw > 0 ? startAtRaw : 0
	const endAtTopRaw = parseFloat(target.getAttribute(END_AT_TOP_ATTR))
	const endAtTop = isFinite(endAtTopRaw) ? endAtTopRaw : 90
	const endAtFullExit = target.hasAttribute(END_AT_FULL_EXIT_ATTR)

	if (animType === 'typewriter' || animType === 'typewriter_words' || animType === 'typewriter_lines') {
		const characters = animType === 'typewriter_lines'
			? Array.from(target.querySelectorAll('.lay-text-line'))
			: animType === 'typewriter_words'
				? getWordLevelAnimationTargets(target)
				: getCharacterLevelAnimationTargets(target)
		target.style.opacity = '1'
		characters.forEach((c) => {
			c.style.opacity = '0'
		})
		target.classList.add('lay-text-animation-initialized')
		const reverse = shouldReverseTypewriterScrub(target, animType)
		prepareTypewriterBackgroundReveal(target, characters, reverse)
		return { type: 'typewriter', characters, reverse, startAt, endAtTop, endAtFullExit }
	}

	if (animType === 'typewriter_fade') {
		const characters = Array.from(target.querySelectorAll('.lay-text-line .char'))
		target.style.opacity = '1'
		characters.forEach((c) => {
			c.style.opacity = '0'
		})
		target.classList.add('lay-text-animation-initialized')
		const baseDelay = parseInt(target.getAttribute('data-duration'), 10) || 40
		const initialDelay = parseInt(target.getAttribute('data-delay'), 10) || 0
		const tweenDuration = 1000
		const totalDuration = Math.max(
			1,
			initialDelay + (characters.length > 0 ? (characters.length - 1) * baseDelay : 0) + tweenDuration,
		)
		const easingName = target.getAttribute('data-animation-easing') || 'easeOutCubic'
		const easingFn =
			typeof anime !== 'undefined' && typeof anime.easing === 'function'
				? anime.easing(easingName)
				: function (n) {
						return n
				  }
		return {
			type: 'typewriter_fade',
			characters,
			startAt,
			endAtTop,
			endAtFullExit,
			baseDelay,
			initialDelay,
			tweenDuration,
			totalDuration,
			easingFn,
		}
	}

	if (animType !== 'fade_whole_text' && animType !== 'fade_and_slide_whole_text') {
		target.style.opacity = '1'
	}
	// Same lazy-load / re-split race as in grid scrub.js: drop any cached
	// timeline so createAnimationTimeline rebuilds against the current DOM
	// rather than against a stale (pre-pretext) split.
	const previous = animationTimelines.get(target)
	if (previous && typeof previous.pause === 'function') previous.pause()
	animationTimelines.delete(target)
	const timeline = createAnimationTimeline(target)
	if (timeline && typeof timeline.pause === 'function') {
		timeline.pause()
	}
	// For masked_*: assert clip once + neutralise the timeline's complete
	// callback so it doesn't unclip when progress hits 1. See scrub.js for
	// the full rationale.
	const isMaskedType =
		animType === 'masked_lines' || animType === 'masked_words' || animType === 'masked_characters'
	const lineMasks = isMaskedType ? target.querySelectorAll('.lay-text-line-mask') : null
	if (lineMasks && lineMasks.length > 0) {
		setLineTextMaskRevealClip(lineMasks, true)
	}
	if (isMaskedType && timeline) {
		timeline.complete = function () {}
	}
	target.classList.add('lay-text-animation-initialized')
	return { type: animType, timeline, startAt, endAtTop, endAtFullExit, lineMasks }
}

function getOverlayNodeFromMaybeJquery($overlay) {
	if (!$overlay) return null
	if ($overlay.nodeType === 1) return $overlay
	if ($overlay[0] && $overlay[0].nodeType === 1) return $overlay[0]
	return null
}

function isOverlayReadyForScrubMeasurements(overlay, force) {
	if (!overlay) return false
	if (!overlay.classList.contains('display-block-overlay')) return false
	if (!overlay.classList.contains('lay-show-overlay')) return false
	if (overlay.classList.contains('lay-overlay-closing')) return false
	if (!force && document.body && document.body.classList.contains('do-page-overlay-transition')) return false
	return true
}

function resetAndDeletePairing(target) {
	cancelIntroAnimation()
	const pairing = pairings.get(target)
	if (pairing && pairing.timeline && typeof pairing.timeline.pause === 'function') {
		pairing.timeline.pause()
	}
	const timeline = animationTimelines.get(target)
	if (timeline && typeof timeline.pause === 'function') {
		timeline.pause()
	}
	animationTimelines.delete(target)
	pairings.delete(target)
	clearTypewriterBackgroundReveal(target)
	syncScrollRootListeners()
	applyPreRevealVisualStateToTarget(target)
	target.classList.remove('text-animation-in-view')
	target.classList.add('text-animation-never-played')
}

function resetPairingsInsideOverlay($overlay) {
	const overlay = getOverlayNodeFromMaybeJquery($overlay)
	if (!overlay) {
		document.querySelectorAll('.lay-overlay [' + SCRUB_ATTR + '="true"]').forEach((target) => {
			resetAndDeletePairing(target)
		})
		return
	}
	overlay.querySelectorAll('[' + SCRUB_ATTR + '="true"]').forEach((target) => {
		resetAndDeletePairing(target)
	})
}

function deletePairingsOutsideTargets(found) {
	for (const target of Array.from(pairings.keys())) {
		if (!found.has(target)) {
			resetAndDeletePairing(target)
		}
	}
}

function refreshPairings(options = {}) {
	const force = !!options.force
	const scopedOverlay = getOverlayNodeFromMaybeJquery(options.overlay)
	const overlayElsRaw = Array.from(document.querySelectorAll('[' + SCRUB_ATTR + '="true"]'))
	const overlayEls = overlayElsRaw.filter((el) => {
		if (el.closest('.lay-row-fixed')) return false
		const overlay = el.closest('.lay-overlay')
		if (!overlay) return false
		if (scopedOverlay && overlay !== scopedOverlay) return false
		return isOverlayReadyForScrubMeasurements(overlay, force)
	})

	if (prefersReducedMotion) {
		overlayEls.forEach((el) => {
			const pairing = buildPairing(el)
			snapPairingToEnd(el, pairing)
		})
		return
	}

	const found = new Set(overlayEls)

	deletePairingsOutsideTargets(found)

	// Always rebuild — same reasoning as grid scrub.js: the DOM may have
	// been re-split (resize, pretext lazy-load finished after Splitting.js
	// fallback) and the existing pairing's anime timeline targets the OLD
	// line/word/char elements which were replaced. Without the rebuild,
	// `applyTimelineProgress` writes to detached nodes and the visible
	// (new) DOM never animates — looks like the scrub silently stops
	// working after resize.
	found.forEach((target) => {
		const prevPairing = pairings.get(target)
		if (prevPairing && prevPairing.type === 'typewriter') {
			clearTypewriterBackgroundReveal(target)
		}
		pairings.set(target, buildPairing(target))
	})

	syncScrollRootListeners()

	if (pairings.size === 0) return
	attachListeners()

	if (options.animateInitialProgress) {
		animateInitialPairingsToCurrentProgress(found)
		return
	}

	pairings.forEach((pairing, target) => {
		const rect = getNaturalViewportRect(target)
		const initCtx = getViewportContext(getScrollContainerForElement(target))
		updatePairing(target, pairing, rect, initCtx)
	})
	onScrollOrResize()
}

const i = () => {
	const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
	prefersReducedMotion = mq.matches

	if (window.laytheme && typeof window.laytheme.on === 'function') {
		window.laytheme.on('texts_are_split_for_page_overlays', refreshPairings)
		window.laytheme.on('page_overlay_before_shown', resetPairingsInsideOverlay)
		window.laytheme.on('overlayshown_animation_ended', function ($overlay) {
			refreshPairings({ force: true, overlay: $overlay, animateInitialProgress: true })
		})
		window.laytheme.on('smoothscroll', onScrollOrResize)
		window.laytheme.on('overlayhidden', resetPairingsInsideOverlay)
	}

	if (mq.addEventListener) {
		mq.addEventListener('change', (e) => {
			prefersReducedMotion = e.matches
			if (prefersReducedMotion) {
				pairings.forEach((pairing, target) => snapPairingToEnd(target, pairing))
			}
		})
	}
}

const text_animation_scrub_for_pageoverlays_controller = {
	i: i,
}

export default text_animation_scrub_for_pageoverlays_controller
